<template>
  <div class="refund-page">
    <!-- PAGE HERO -->
    <section class="page-hero">
      <div class="page-hero-inner">
        <h1>{{ heading }}</h1>
        <p>
          We want you to love your new eyewear. If you're not completely
          satisfied, here's everything you need to know.
        </p>
      </div>
    </section>

    <!-- MOBILE SECTION NAV (pill tabs) -->
    <nav
      class="mobile-section-nav"
      ref="mobileNav"
      aria-label="Policy sections (mobile)"
    >
      <a
        v-for="s in sections"
        :key="'m-' + s.id"
        :href="'#' + s.id"
        :class="{ active: activeId === s.id }"
        @click.prevent="scrollToSection(s.id)"
        >{{ s.short }}</a
      >
    </nav>

    <!-- MAIN LAYOUT -->
    <div class="main-layout">
      <!-- Sidebar -->
      <aside class="sidebar" aria-label="On this page">
        <p class="sidebar-label">On this page</p>
        <ul class="sidebar-nav">
          <li v-for="s in sections" :key="'s-' + s.id">
            <a
              :href="'#' + s.id"
              :class="{ active: activeId === s.id }"
              @click.prevent="scrollToSection(s.id)"
              >{{ s.long }}</a
            >
          </li>
        </ul>
      </aside>

      <!-- Policy content -->
      <div class="policy-content">
        <!-- Returns -->
        <section class="policy-section reveal" id="returns">
          <p class="section-eyebrow">Policy</p>
          <h2 class="section-title">Returns</h2>
          <p>
            Items may be returned within
            <strong>14 days of delivery</strong>. A return shipping fee of
            <strong>$9.95</strong> will be deducted from your refund.
          </p>
          <div class="callout callout-warning">
            <strong>Please note:</strong> Final sale items are non-returnable.
            International orders are not eligible for returns.
          </div>
        </section>

        <!-- Eligibility -->
        <section class="policy-section reveal" id="eligibility">
          <p class="section-eyebrow">Requirements</p>
          <h2 class="section-title">Eligibility requirements</h2>
          <p>
            To be eligible for a return, items must be unworn, unused, and in
            their original condition. Your return must include all of the
            following:
          </p>
          <ul class="checklist">
            <li>The original sunglasses or eyeglasses</li>
            <li>Glasses case</li>
            <li>Cleaning cloth</li>
            <li>Any accompanying paperwork</li>
            <li>Protective plastic (if applicable)</li>
            <li>Brand's box (if applicable)</li>
            <li>Outer box with manufacturer tags (if applicable)</li>
            <li>
              Plastic protective covers on the temple(s), securely attached
            </li>
          </ul>
          <div class="callout">
            <strong>Important:</strong> The original tag stating
            <em>"If removed, no returns"</em> must remain attached to the frame.
            Returns with this tag removed will not qualify for a full refund.
          </div>
          <div class="callout callout-warning">
            <strong>Restocking fee:</strong> If any item, accessory, packaging
            component, or required tag is missing — or if the item shows signs
            of wear — a <strong>20% restocking fee</strong> will be applied to
            your refund.
          </div>
          <div class="callout callout-danger">
            <strong>Refund denied:</strong> If the returned package does not
            contain the actual eyewear, the refund will be denied and the
            package will be returned to your original shipping address.
          </div>
        </section>

        <!-- Initiate -->
        <section class="policy-section reveal" id="initiate">
          <p class="section-eyebrow">Process</p>
          <h2 class="section-title">How to initiate a return</h2>
          <div class="steps">
            <div class="step" v-for="(step, i) in steps" :key="'step-' + i">
              <div class="step-num">{{ i + 1 }}</div>
              <div class="step-body">
                <strong>{{ step.title }}</strong>
                <p v-html="step.body"></p>
              </div>
            </div>
          </div>
        </section>

        <!-- Refunds -->
        <section class="policy-section reveal" id="refunds">
          <p class="section-eyebrow">Refunds</p>
          <h2 class="section-title">Refunds</h2>
          <p>
            Once we receive and inspect your return, we'll notify you by email.
            If the item is in perfect condition — brand new and unused — your
            refund will be issued to your original payment method within
            <strong>1–5 business days</strong>.
          </p>
          <p>
            Once your refund is processed, you'll receive a confirmation email.
            Refunds may take a few additional business days to appear on your
            statement, depending on your financial institution.
          </p>
          <div class="callout callout-warning">
            <strong>Non-refundable:</strong> Shipping fees are not refundable,
            unless the item arrived damaged or incorrect. If we receive items
            that have been used, damaged, or are missing accessories, we may not
            be able to issue a full refund.
          </div>
        </section>

        <!-- Exchanges -->
        <section class="policy-section reveal" id="exchanges">
          <p class="section-eyebrow">Exchanges</p>
          <h2 class="section-title">Exchanges</h2>
          <p>
            If you'd prefer a different eligible frame or size, contact our
            support team within 14 days of delivery. Once approved, we'll
            arrange a direct exchange with no additional shipping charge. Final
            sale items and international orders are not eligible for exchange.
          </p>
        </section>

        <!-- Damaged -->
        <section class="policy-section reveal" id="damaged">
          <p class="section-eyebrow">Issues</p>
          <h2 class="section-title">Damaged or incorrect items</h2>
          <p>
            If your order arrives damaged or you received the wrong item, please
            contact us immediately with photos of the product. We will resolve
            the issue as quickly as possible.
          </p>
          <p>
            Reach us at
            <a href="mailto:support@hooliganseyewear.com" class="email-link"
              >support@hooliganseyewear.com</a
            >
            — we're happy to help.
          </p>
        </section>

        <!-- Contact CTA -->
        <div class="contact-cta reveal">
          <div class="contact-cta-text">
            <h3>Still have questions?</h3>
            <p>
              Our team is ready to help with any return or refund inquiries.
            </p>
          </div>
          <a
            href="mailto:support@hooliganseyewear.com"
            class="btn-outline-white"
            >Contact support</a
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "./refund_policy.css";
import { useStaticPageSeo } from "~/core/composables/useStaticPageSeo";

export default {
  name: "RefundPolicy",
  setup() {
    return useStaticPageSeo({
      title: "Returns & Refund Policy | Hooligans Eyewear",
      heading: "Refund & Return Policy",
      description:
        "Read the Hooligans Eyewear 14-day return policy, eligibility requirements, $9.95 return fee, exchanges, and refund processing times.",
    });
  },
  data() {
    return {
      activeId: "returns",
      sections: [
        { id: "returns", short: "Returns", long: "Returns" },
        {
          id: "eligibility",
          short: "Eligibility",
          long: "Eligibility requirements",
        },
        {
          id: "initiate",
          short: "How to return",
          long: "How to initiate a return",
        },
        { id: "refunds", short: "Refunds", long: "Refunds" },
        { id: "exchanges", short: "Exchanges", long: "Exchanges" },
        {
          id: "damaged",
          short: "Damaged items",
          long: "Damaged or incorrect items",
        },
      ],
      steps: [
        {
          title: "Contact us",
          body: 'Email <a href="mailto:support@hooliganseyewear.com" class="email-link">support@hooliganseyewear.com</a> with your order number and reason for return.',
        },
        {
          title: "Get your return label",
          body: "Once your request is approved, we'll email you a prepaid return label and detailed instructions.",
        },
        {
          title: "Ship your item",
          body: "Pack the item securely with all required accessories and drop it off at the carrier location.",
        },
        {
          title: "Receive your refund",
          body: "Once we receive and inspect your return, we'll process your refund within 1\u20135 business days.",
        },
      ],
    };
  },
  mounted() {
    if (typeof window === "undefined") return;

    this._reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this._onScroll = () => this._updateActive();
    window.addEventListener("scroll", this._onScroll, { passive: true });
    window.addEventListener("resize", this._onScroll, { passive: true });
    // Run once after paint so sections have measured positions.
    this.$nextTick(() => this._updateActive());
    this._setupRevealObserver();
  },
  beforeUnmount() {
    if (typeof window === "undefined") return;
    if (this._onScroll) {
      window.removeEventListener("scroll", this._onScroll);
      window.removeEventListener("resize", this._onScroll);
    }
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
  },
  methods: {
    _setupRevealObserver() {
      const els = this.$el.querySelectorAll(".reveal");
      if (!els.length) return;

      if (this._reduceMotion || typeof IntersectionObserver === "undefined") {
        els.forEach((el) => el.classList.add("visible"));
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible");
              this._revealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
      );
      els.forEach((el) => this._revealObserver.observe(el));
    },
    // Click-scroll offset. Original HTML used 60/24 because its only
    // sticky strip was the mobile pill nav (~60px tall). We keep 24 on
    // desktop (unchanged) and use 80 on mobile so the sticky strip
    // (~69px tall) + a small gap clears fully, leaving the section's
    // eyebrow + heading fully visible under it (not hidden).
    _getClickOffset() {
      if (typeof window === "undefined") return 24;
      return window.innerWidth <= 900 ? 80 : 24;
    },

    // Scroll-spy threshold: a section is "current" once its top has
    // passed 120px from the viewport top. This is the exact rule from
    // the original HTML so the active highlight advances at the same
    // moment on every viewport.
    _updateActive() {
      const els = this.sections
        .map((s) => ({ id: s.id, el: document.getElementById(s.id) }))
        .filter((x) => x.el);
      if (!els.length) return;

      let current = els[0].id;
      for (const { id, el } of els) {
        if (el.getBoundingClientRect().top <= 120) current = id;
      }
      if (current !== this.activeId) {
        this.activeId = current;
        this.$nextTick(() => this._scrollActiveIntoView());
      }
    },

    // Horizontally centers the active link inside its scrollable parent
    // (the sticky pill strip on mobile, or the legacy mobileNav). Safe
    // to call when the parent is not horizontally scrollable — it just
    // no-ops in that case.
    _scrollActiveIntoView() {
      if (typeof document === "undefined") return;
      const root = this.$el;
      if (!root) return;
      const candidates = [
        root.querySelector(".sidebar-nav"),
        this.$refs.mobileNav,
      ].filter(Boolean);
      for (const nav of candidates) {
        if (!nav.offsetWidth) continue;
        if (nav.scrollWidth <= nav.clientWidth) continue;
        const active = nav.querySelector("a.active");
        if (!active) continue;
        const left = active.offsetLeft;
        const width = active.offsetWidth;
        nav.scrollLeft = left - nav.offsetWidth / 2 + width / 2;
      }
    },

    // Smooth-scroll to a section. Mirrors the original:
    //   const top = target.getBoundingClientRect().top + scrollY - offset;
    //   window.scrollTo({ top, behavior: 'smooth' });
    scrollToSection(id) {
      if (typeof window === "undefined") return;
      const target = document.getElementById(id);
      if (!target) return;
      const offset = this._getClickOffset();
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
      this.activeId = id;
      this.$nextTick(() => this._scrollActiveIntoView());
    },
  },
};
</script>
